<template>
  <v-card class="nav-columns" :style="{width: panelWidth}" data-cy="nav">
    <!-- one line: the search, who you are, then the menu's buttons and logout top right -->
    <div class="d-flex align-center pa-3 pb-2">
      <v-text-field
        v-if="!fewPages"
        v-model="search"
        dense
        outlined
        clearable
        hide-details
        autofocus
        prepend-inner-icon="mdi-magnify"
        :placeholder="placeholder"
        class="nav-search mr-4"
        data-cy="nav-search"
        @keydown.enter="openFirstMatch"
      />
      <div class="nav-who">
        <slot name="account" :parts="['who']"/>
      </div>
      <div class="d-flex align-center ml-4">
        <slot name="actions"/>
        <slot name="account" :parts="['logout']"/>
      </div>
    </div>
    <v-divider/>

    <nav-customer-choice
      :email="emailSearch"
      :customers="customerChoices"
      @pick="openCustomerPage"
      @close="customerChoices = []"
    />

    <div v-if="search" class="nav-results" data-cy="nav-results">
      <v-list dense>
        <v-list-item v-if="emailSearch" disabled>
          <v-list-item-content>Enter opens the customer "{{ emailSearch }}"</v-list-item-content>
        </v-list-item>
        <v-list-item v-else-if="matches.length === 0" disabled>
          <v-list-item-content>no pages match "{{ search }}"</v-list-item-content>
        </v-list-item>
        <nav-item
          v-for="match of matches"
          :key="match.path.join('/')"
          :item="match.item"
          :trail="match.path.slice(0, -1).join(' › ')"
          :location="location"
        />
      </v-list>
    </div>

    <div v-else class="d-flex nav-column-row">
      <div
        v-for="(column, level) of columns"
        :key="level"
        class="nav-column"
        :data-cy="`nav-column-${level}`"
      >
        <v-list dense class="py-0">
          <template v-for="row of column.rows">
            <v-subheader v-if="row.header" :key="row.key">{{ row.header }}</v-subheader>
            <v-list-item v-else-if="row.hint" :key="row.key" disabled>
              <v-list-item-content class="nav-summary text-caption">{{ row.hint }}</v-list-item-content>
            </v-list-item>
            <v-list-item
              v-else
              :key="row.key"
              :to="row.to || undefined"
              :exact="!!row.to"
              :class="{'nav-selected': selected[level] === row.key}"
              :data-cy="`nav-${row.cy}`"
              @click="row.to ? undefined : select(level, row.key)"
              @mouseenter="hover(level, row.hasNext ? row.key : null)"
              @mouseleave="cancelHover"
            >
              <v-list-item-icon v-if="row.icon" class="mr-3">
                <v-icon>{{ row.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title :class="{'font-weight-medium': level === 0}">
                  {{ row.title }}<span v-if="row.idText" class="text-caption"> · {{ row.idText }}</span>
                  <v-icon v-if="row.starred" x-small color="amber darken-2">mdi-star</v-icon>
                </v-list-item-title>
                <v-list-item-subtitle class="nav-summary">{{ row.summary }}</v-list-item-subtitle>
              </v-list-item-content>
              <!-- over the end of the row on hover, so they take no room from the summary -->
              <div v-if="row.section && editing" class="nav-row-actions nav-row-actions-shown">
                <v-btn
                  icon
                  x-small
                  title="name this section and its subtitle"
                  :data-cy="`nav-edit-${row.cy}`"
                  @click.prevent.stop="$emit('edit-section', row.section)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div v-if="row.item" class="nav-row-actions" :class="{'nav-row-actions-shown': editing}">
                <v-btn
                  v-if="row.to"
                  icon
                  x-small
                  :title="row.starred ? 'unstar' : 'star this page to keep it at the top of the menu'"
                  :data-cy="`nav-star-${row.cy}`"
                  @click.prevent.stop="$emit('star', row.page)"
                >
                  <v-icon small :color="row.starred ? 'amber darken-2' : undefined">
                    {{ row.starred ? 'mdi-star' : 'mdi-star-outline' }}
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="editing"
                  icon
                  x-small
                  title="name this page and its description"
                  :data-cy="`nav-edit-page-${row.cy}`"
                  @click.prevent.stop="$emit('edit-page', row.item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="editing"
                  icon
                  x-small
                  title="move this page to another part of the menu"
                  :data-cy="`nav-move-${row.cy}`"
                  @click.prevent.stop="$emit('move', row.item)"
                >
                  <v-icon small>mdi-folder-move-outline</v-icon>
                </v-btn>
              </div>
              <v-list-item-action v-if="row.hasNext" class="my-0 ml-1">
                <v-btn
                  icon
                  small
                  :data-cy="`nav-open-${row.cy}`"
                  @click.prevent.stop="select(level, row.key)"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </div>
    </div>
    <!-- the way back to the old menu, and the test/live switch for developers -->
    <div class="nav-bottom d-flex align-center px-3 py-1">
      <slot name="footer"/>
      <v-spacer/>
      <slot name="account" :parts="['apiSwitch']"/>
    </div>
  </v-card>
</template>

<script>
import NavCustomerChoice from '@/components/NavCustomerChoice.vue';
import NavItem from '@/components/NavItem.vue';
import navSearch from '@/components/nav-search';
import {flattenNav, hasFewPages, isShortMenu, isStarred, pageTitle} from '@/nav-helpers';

const HOVER_DELAY_MS = 250;
/** a column's width when the screen has room: a summary fits on a line */
const COLUMN_WIDTH = 420;
const PANEL_MIN_WIDTH = 700;

/**
 * The menu on a wide screen, as columns: the sections, then the pages of the chosen
 * section, then the pages opened from the chosen page, and so on. It opens on the user's
 * starred and recent pages. Hovering or clicking the arrow opens the next column; clicking a
 * page goes to it.
 */
export default {
  name: 'NavColumns',
  components: {NavCustomerChoice, NavItem},
  mixins: [navSearch],
  props: {
    sections: {type: Array, required: true},
    /** starred and recently visited pages: [{item, to, page}] */
    starredPages: {type: Array, required: true},
    recentPages: {type: Array, required: true},
    /** the starred pages as saved: [{name, params}] */
    starred: {type: Array, required: true},
    location: {type: Function, required: true},
    /** show the buttons to move pages and edit the names and descriptions of pages and sections */
    editing: {type: Boolean, default: false},
  },
  data() {
    return {
      selected: ['quick'],
      hoverTimer: null,
    };
  },
  computed: {
    columns() {
      const columns = [{kind: 'rows', rows: this.isShort ? this.shortList : this.firstColumn}];
      const [first, ...pages] = this.selected;
      if (this.isShort) {
        return columns;
      }
      if (first === 'quick') {
        return [...columns, {kind: 'rows', rows: this.quickColumn}];
      }
      const section = this.sections.find(s => `section:${s.id}` === first);
      if (!section) {
        return columns;
      }
      let items = section.items;
      columns.push({kind: 'rows', rows: items.map(item => this.pageRow(item))});
      for (const name of pages) {
        const item = items.find(i => i.name === name);
        if (!item || !(item.children || []).length) {
          break;
        }
        items = item.children;
        columns.push({kind: 'rows', rows: items.map(child => this.pageRow(child))});
      }
      return columns;
    },
    /**
     * room for each open column, up to the whole screen, and never so narrow that the top bar's
     * controls stack up; search results take the whole screen
     */
    panelWidth() {
      const width = this.search ? 'calc(100vw - 24px)' : `${Math.max(this.columns.length * COLUMN_WIDTH, PANEL_MIN_WIDTH)}px`;
      return `min(${width}, calc(100vw - 24px))`;
    },
    firstColumn() {
      return [
        {
          key: 'quick', cy: 'quick', title: 'Starred & recent', icon: 'mdi-star-outline',
          summary: 'Your starred pages and the last pages you opened', hasNext: true,
        },
        ...this.sections.map(section => ({
          key: `section:${section.id}`, cy: `section:${section.id}`, title: section.title,
          icon: section.icon, summary: section.summary, hasNext: true, section,
        })),
      ];
    },
    /** a role with only a few pages has nothing to search */
    fewPages() {
      return hasFewPages(this.sections);
    },
    /** a role with a few pages sees them all at once */
    isShort() {
      return isShortMenu(this.sections);
    },
    /** the short menu: starred, recent, then each section's pages under its name */
    shortList() {
      const shortcuts = (group, pages) => pages
        .map(({item, to, page}) => this.pageRow(item, to, page))
        .map(row => ({...row, key: `${group}-${row.key}`}));
      const group = (key, header, rows) => (rows.length ? [{key: `${key}-header`, header}, ...rows] : []);
      return [
        ...group('starred', 'Starred', shortcuts('starred', this.starredPages)),
        ...group('recent', 'Recent', shortcuts('recent', this.recentPages)),
        ...this.sections.flatMap(section => group(
          `section:${section.id}`,
          section.title,
          flattenNav([section]).map(({item}) => ({...this.pageRow(item), hasNext: false})),
        )),
      ];
    },
    quickColumn() {
      const shortcuts = pages => pages.map(({item, to, page}) => this.pageRow(item, to, page));
      return [
        {key: 'starred-header', header: 'Starred'},
        ...(this.starredPages.length
          ? shortcuts(this.starredPages)
          : [{key: 'starred-hint', hint: 'Star a page with the ☆ beside it to keep it here.'}]),
        {key: 'recent-header', header: 'Recent'},
        ...(this.recentPages.length
          ? shortcuts(this.recentPages)
          : [{key: 'recent-hint', hint: 'The pages you open will show here.'}]),
      ];
    },
  },
  beforeDestroy() {
    this.cancelHover();
  },
  methods: {
    /** a page's row; `to` and `page` are given for a starred or recent page, which may be one record */
    pageRow(item, to, page) {
      page = page || {name: item.name, params: {}};
      return {
        // the name, which the columns are opened by; a starred customer and a recent one differ by id
        key: Object.keys(page.params).length ? `${item.name}${JSON.stringify(page.params)}` : item.name,
        cy: item.name,
        item,
        page,
        title: pageTitle(item),
        summary: item.summary,
        to: to || this.location(item),
        starred: isStarred(this.starred, page),
        idText: item.needs ? Object.values(page.params).join(' ') : '',
        // a shortcut leaves the pages under it to the section
        hasNext: !to && (item.children || []).length > 0,
      };
    },
    select(level, key) {
      this.cancelHover();
      this.selected = [...this.selected.slice(0, level), key];
    },
    /** hovering a row opens its column (or closes deeper ones), after a pause so a diagonal move to the next column doesn't switch */
    hover(level, key) {
      this.cancelHover();
      this.hoverTimer = setTimeout(() => {
        if (key) {
          this.select(level, key);
        } else if (level > 0) {
          this.selected = this.selected.slice(0, level);
        }
      }, HOVER_DELAY_MS);
    },
    cancelHover() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    },
  },
};
</script>

<style scoped>
/* the panel fits the screen below the app bar: the top bar takes what it needs, and the
   columns (or search results) fill the rest, each scrolling on its own */
.nav-columns {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 68px);
}

.nav-column-row,
.nav-results {
  flex: 1 1 auto;
  min-height: 0;
}

.nav-search {
  flex: 0 1 320px;
}

.nav-who {
  flex: 1 1 0;
  min-width: 0;
}

.nav-bottom {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.nav-column {
  /* equal shares of the panel, which grows with each column up to the screen's width */
  flex: 1 1 0;
  min-width: 0;
  overflow-y: auto;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.nav-results {
  overflow-y: auto;
  column-width: 380px;
}

.nav-results .v-list > * {
  break-inside: avoid;
}

.nav-summary {
  white-space: normal;
}

.nav-selected {
  background: rgba(0, 0, 0, 0.08);
}


.nav-row-actions {
  display: none;
  position: absolute;
  top: 4px;
  right: 44px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.v-list-item:hover .nav-row-actions,
.nav-row-actions-shown {
  display: flex;
}

/* while editing they stay, in the row */
.nav-row-actions-shown {
  position: static;
  box-shadow: none;
}
</style>
